import { useEffect, useMemo, useState } from 'react'
import {
  Download,
  Edit2,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  Shield,
  Trash2,
} from 'lucide-react'
import { adminApi } from '../../lib/api'
import type { FileRecord } from '../../types'
import FileMetadataModal, { type FileMetadataFormValues } from '../files/FileMetadataModal'
import { useToast } from '../ui/ToastProvider'

interface FilesTabProps {
  tripId: string
  preloadedData?: any[]
  userId?: string // Waybook user ID to link files to
}

interface TripEventOption {
  id: string
  title: string
}

const EVENT_FILTER_ALL = 'all'
const EVENT_FILTER_TRIP_ONLY = '__trip_only'

const PRIVACY_LABELS: Record<FileRecord['privacy'], string> = {
  public: 'Public',
  trip_members: 'Trip members',
  event_members: 'Event members',
  private: 'Private',
  custom: 'Custom',
}

const PRIVACY_COLORS: Record<FileRecord['privacy'], string> = {
  public: 'bg-green-100 text-green-800',
  trip_members: 'bg-blue-100 text-blue-800',
  event_members: 'bg-purple-100 text-purple-800',
  private: 'bg-gray-100 text-gray-700',
  custom: 'bg-amber-100 text-amber-800',
}

function parseTags(input: any): string[] {
  if (!input) return []
  if (Array.isArray(input)) return input.filter(Boolean).map(String)
  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input)
      if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String)
    } catch {
      return input
        .split(',')
        .map(value => value.trim())
        .filter(Boolean)
    }
  }
  return []
}

function normalizeFile(data: any): FileRecord {
  return {
    id: data.id,
    filename: data.filename || data.name || 'Untitled file',
    originalFilename: data.original_filename || data.originalFilename,
    description: data.description || '',
    tags: parseTags(data.tags),
    privacy: data.privacy || 'private',
    url: data.url || data.file_url,
    externalUrl: data.external_url,
    filePath: data.file_path,
    fileType: data.file_type || data.type,
    fileSize: data.file_size || data.size,
    tripId: data.trip_id || data.tripId,
    eventId: data.event_id || data.eventId,
    uploadedBy: data.uploaded_by || data.uploadedBy,
    createdAt: data.created_at || data.createdAt || new Date().toISOString(),
    updatedAt: data.updated_at || data.updatedAt,
  }
}

function formatFileSize(bytes?: number) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(1)} KB`
  const mb = kb / 1024
  if (mb < 1024) return `${mb.toFixed(1)} MB`
  const gb = mb / 1024
  return `${gb.toFixed(1)} GB`
}

function formatDate(value?: string | number) {
  if (!value) return '—'
  const date = typeof value === 'number'
    ? new Date(value)
    : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString()
}

export default function FilesTab({ tripId, preloadedData, userId }: FilesTabProps) {
  const { showToast } = useToast()
  const [files, setFiles] = useState<FileRecord[]>([])
  const [events, setEvents] = useState<TripEventOption[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filterEventId, setFilterEventId] = useState<string>(EVENT_FILTER_ALL)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const [activeFile, setActiveFile] = useState<FileRecord | null>(null)

  useEffect(() => {
    if (preloadedData && Array.isArray(preloadedData)) {
      setFiles(preloadedData.map(normalizeFile))
      setLoading(false)
    }
  }, [preloadedData])

  useEffect(() => {
    loadEvents()
    loadFiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId])

  const loadEvents = async () => {
    try {
      const response = await adminApi.getTripEvents(tripId)
      const rawEvents = response.data?.data || response.data || []
      const mapped = Array.isArray(rawEvents)
        ? rawEvents.map((event: any) => ({
            id: event.id,
            title: event.title || 'Untitled event',
          }))
        : []
      setEvents(mapped)
    } catch (err) {
      console.error('Failed to load events for files tab:', err)
    }
  }

  const loadFiles = async () => {
    setRefreshing(true)
    try {
      const response = await adminApi.getTripFiles(tripId)
      const payload = response.data?.data || response.data || {}
      const rawFiles = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.files)
          ? payload.files
          : payload.items || []
      const normalized = rawFiles.map(normalizeFile)
      setFiles(normalized)
      setError(null)
    } catch (err: any) {
      console.error('Failed to load files:', err)
      setError(err?.response?.data?.error || 'Failed to load files.')
      if (!preloadedData) {
        setFiles([])
      }
    } finally {
      setRefreshing(false)
      setLoading(false)
    }
  }

  const filteredFiles = useMemo(() => {
    return files.filter((file) => {
      if (filterEventId === EVENT_FILTER_ALL) return true
      if (filterEventId === EVENT_FILTER_TRIP_ONLY) return !file.eventId
      return file.eventId === filterEventId
    })
  }, [files, filterEventId])

  const getEventTitle = (eventId?: string) => {
    if (!eventId) return 'Trip-wide'
    const event = events.find(e => e.id === eventId)
    return event ? event.title : 'Event'
  }

  const openCreateModal = () => {
    setModalMode('create')
    setActiveFile(null)
    setIsModalOpen(true)
  }

  const openEditModal = (file: FileRecord) => {
    setModalMode('edit')
    setActiveFile(file)
    setIsModalOpen(true)
  }

  const handleDelete = async (file: FileRecord) => {
    if (!confirm(`Delete file "${file.filename}"? This action cannot be undone.`)) {
      return
    }
    try {
      await adminApi.deleteTripFile(tripId, file.id)
      await loadFiles()
    } catch (err: any) {
      console.error('Failed to delete file:', err)
      showToast(err?.response?.data?.error || 'Failed to delete file.', { type: 'error' })
    }
  }

  const handleModalSubmit = async (values: FileMetadataFormValues) => {
    if (modalMode === 'create') {
      if (values.source === 'upload') {
        if (!values.file) {
          throw new Error('Select a file to upload.')
        }
        await adminApi.uploadFile(values.file, {
          trip_id: tripId,
          event_id: values.eventId || undefined,
          user_id: userId,  // Link file to trip owner
          privacy: values.privacy,
          description: values.description || undefined,
          tags: values.tags,
        })
      } else {
        await adminApi.createTripFile(tripId, {
          filename: values.filename,
          original_filename: values.filename,
          external_url: values.externalUrl,
          description: values.description || undefined,
          tags: values.tags,
          privacy: values.privacy,
          event_id: values.eventId || undefined,
          file_type: values.fileType,
          file_size: values.fileSize,
        })
      }
    } else {
      if (!activeFile) {
        throw new Error('No file selected for editing.')
      }
      await adminApi.updateFile(activeFile.id, {
        filename: values.filename,
        description: values.description || null,
        tags: values.tags,
        privacy: values.privacy,
        event_id: values.eventId || null,
        external_url: values.source === 'external' ? values.externalUrl : null,
        file_type: values.fileType,
        file_size: values.fileSize,
      })
    }

    await loadFiles()
    setIsModalOpen(false)
    setActiveFile(null)
    
    if (modalMode === 'create') {
      showToast('File uploaded successfully!', { type: 'success' })
    } else {
      showToast('File updated successfully!', { type: 'success' })
    }
  }

  const storageLabel = (file: FileRecord) => (file.externalUrl ? 'External' : 'R2')

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Trip Files</h3>
          <p className="text-sm text-gray-500">
            Upload documents, tickets, and media to R2 or link existing external assets.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <select
              value={filterEventId}
              onChange={(e) => setFilterEventId(e.target.value)}
              className="input"
            >
              <option value={EVENT_FILTER_ALL}>All files</option>
              <option value={EVENT_FILTER_TRIP_ONLY}>Trip only</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>
                  {event.title}
                </option>
              ))}
            </select>
            {refreshing && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          </div>
          <button
            onClick={openCreateModal}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add File
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading files...</div>
      ) : filteredFiles.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <FileText className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          <h4 className="text-gray-700 font-medium">No files yet</h4>
          <p className="text-sm text-gray-500 mt-1">
            Files uploaded here will be available to the trip members based on privacy.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredFiles.map((file) => (
            <div
              key={file.id}
              className="border border-gray-200 rounded-lg px-4 py-4 bg-white shadow-sm hover:shadow transition-shadow"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900 truncate">
                      {file.filename}
                    </h4>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${PRIVACY_COLORS[file.privacy]}`}>
                      <Shield className="h-3 w-3" />
                      {PRIVACY_LABELS[file.privacy]}
                    </span>
                    <span className="text-xs text-gray-400">
                      {storageLabel(file)}
                    </span>
                  </div>
                  {file.description && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {file.description}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-2">
                    <span>{getEventTitle(file.eventId)}</span>
                    <span>{file.fileType || 'Unknown type'}</span>
                    <span>{formatFileSize(file.fileSize)}</span>
                    <span>Uploaded {formatDate(file.createdAt)}</span>
                    {file.uploadedBy && (
                      <span>by {file.uploadedBy}</span>
                    )}
                  </div>
                  {file.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {file.tags.map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 flex-shrink-0">
                  {(file.url || file.externalUrl) && (
                    <a
                      href={file.url || file.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open
                    </a>
                  )}
                  {file.url && (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </a>
                  )}
                  <button
                    onClick={() => openEditModal(file)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50"
                  >
                    <Edit2 className="h-4 w-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(file)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-red-200 rounded-md text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <FileMetadataModal
        open={isModalOpen}
        mode={modalMode}
        onClose={() => {
          setIsModalOpen(false)
          setActiveFile(null)
        }}
        onSubmit={handleModalSubmit}
        initialData={activeFile ?? undefined}
        events={events}
        contextLabel="Trip-level file management"
      />
    </div>
  )
}
